import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import type { GateResult } from "@iamu/shared";
import {
  Copy,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  HelpCircle,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { isValidUuid, cn } from "@/lib/utils";
import { Page } from "@/components/layout/page";
import { ActionBar } from "@/components/layout/action-bar";
import { useSetBreadcrumb } from "@/components/layout/breadcrumb-context";
import { useProject } from "@/components/project-context";
import { EmptyState } from "@/components/empty-state";
import { useProjectAccess } from "@/lib/project-access";
import { Button } from "@/components/ui/button";
import { Chip } from "@/components/ui/chip";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  BASELINE_MODE_LABELS,
  GateStatusChip,
  shortSha,
  UncommittedChip,
} from "@/components/gate/gate-vocabulary";
import { GateConditionsTable } from "@/components/gate/gate-conditions";
import { GateCoverage } from "@/components/gate/gate-coverage";
import { GateBuckets } from "@/components/gate/gate-buckets";
import { GateHistoryList } from "@/components/gate/gate-history";
import type { GateHistoryView } from "@/components/gate/gate-data";

import { Kicker } from "@/components/ui/kicker";

type Mode = "previous_run" | "previous_build";
type Env = "same" | "any";
const STORED = "stored";

function StatusIcon({ status }: { status?: string }) {
  if (status === "passed") {
    return (
      <CheckCircle2 className="size-5 text-finding-helps" aria-hidden="true" />
    );
  }
  if (status === "failed") {
    return (
      <XCircle className="size-5 text-severity-critical" aria-hidden="true" />
    );
  }
  return <HelpCircle className="size-5 text-sky-500" aria-hidden="true" />;
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-3">
      <Kicker as="h2">{title}</Kicker>
      {children}
    </section>
  );
}

/**
 * A build's quality gate: status and headline first, then the
 * conditions, coverage by scenario, every problem in its bucket, and the
 * recent history on the same branch and environment. The baseline can be
 * changed for an ad-hoc comparison, which is never stored.
 */
export function BuildDetailPage() {
  const project = useProject();
  const { buildId = "" } = useParams<{ buildId: string }>();
  const isValidId = isValidUuid(buildId);
  const utils = trpc.useUtils();
  const [mode, setMode] = useState<Mode | typeof STORED>(STORED);
  const [envOverride, setEnvOverride] = useState<Env | null>(null);
  const adHoc = mode !== STORED || envOverride !== null;

  const { data, isLoading, error, refetch } = trpc.build.get.useQuery(
    {
      projectId: project.id,
      buildId,
    },
    { enabled: isValidId },
  );
  const storedEnv =
    (data?.evaluation?.result as GateResult | undefined)?.baseline
      .environment ?? "same";
  const environment = envOverride ?? storedEnv;
  const adHocQuery = trpc.gate.evaluateAdHoc.useQuery(
    {
      projectId: project.id,
      buildId,
      baseline: {
        ...(mode !== STORED ? { mode } : {}),
        environment,
      },
    },
    { enabled: adHoc && !!data },
  );
  const { data: historyRaw } = trpc.gate.history.useQuery(
    {
      projectId: project.id,
      branch: data?.build.gitBranch ?? null,
      environment: data?.build.environment ?? null,
      limit: 10,
    },
    { enabled: !!data },
  );
  const { canEdit, isViewer } = useProjectAccess();
  const evaluate = trpc.gate.evaluate.useMutation({
    onSuccess: () => {
      toast.success("Gate re-evaluated");
      void utils.build.get.invalidate();
      void utils.build.list.invalidate();
      void utils.gate.history.invalidate();
    },
    onError: (err) => toastTrpcError(err, "Failed to evaluate gate"),
  });

  const stored = data?.evaluation?.result as GateResult | undefined;
  const result = (adHoc ? adHocQuery.data : stored) as GateResult | undefined;
  const runsByScenario = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const r of data?.runs ?? []) {
      map.set(r.scenarioId, [...(map.get(r.scenarioId) ?? []), r.id]);
    }
    return map;
  }, [data?.runs]);

  const back = {
    to: `/projects/${project.id}/walkthroughs/builds`,
    label: "Builds",
  };

  useSetBreadcrumb(data?.build?.label);

  if (isLoading) {
    return (
      <Page title="Build" back={back}>
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </Page>
    );
  }
  if (!isValidId || error?.data?.code === "NOT_FOUND" || !data) {
    return (
      <Page title="Build not found" back={back}>
        <EmptyState
          title="This build doesn't exist"
          description="It may have been removed, or it belongs to another project."
        />
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Build not found" back={back}>
        <EmptyState
          icon={AlertCircle}
          title="Failed to load build"
          description="Something went wrong while loading this build. Please try again."
          actions={
            <Button onClick={() => refetch()} variant="outline">
              Try again
            </Button>
          }
        />
      </Page>
    );
  }

  const { build } = data;
  const meta = [
    [build.gitBranch, shortSha(build.gitSha)].filter(Boolean).join(" "),
    build.environment,
    `${data.runs.length} ${data.runs.length === 1 ? "run" : "runs"}`,
    build.status === "closed" ? "closed" : "open",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Page
      title={build.label}
      back={back}
      eyebrow={
        <div className="flex flex-wrap items-center gap-2">
          <GateStatusChip status={result?.status} />
          {build.gitDirty && <UncommittedChip hash={build.worktreeHash} />}
          {adHoc && <Chip tone="primary">Ad-hoc comparison, not saved</Chip>}
        </div>
      }
      description={meta}
      actions={
        <ActionBar
          primary={
            canEdit ? (
              <Button
                disabled={evaluate.isPending}
                onClick={() =>
                  evaluate.mutate({
                    projectId: project.id,
                    buildId: build.id,
                  })
                }
              >
                <RefreshCw
                  className={cn(
                    "mr-2 h-4 w-4",
                    evaluate.isPending && "animate-spin",
                  )}
                  aria-hidden="true"
                />
                Re-evaluate
              </Button>
            ) : undefined
          }
          secondary={[
            <Button
              key="copy"
              variant="outline"
              disabled={!result}
              onClick={() => {
                void navigator.clipboard
                  .writeText(JSON.stringify(result, null, 2))
                  .then(() => toast.success("Gate result copied as JSON"));
              }}
            >
              <Copy className="mr-2 h-4 w-4" aria-hidden="true" />
              Copy JSON
            </Button>,
          ]}
        />
      }
    >
      {!result ? (
        adHoc && adHocQuery.isLoading ? (
          <Skeleton className="h-64 w-full" />
        ) : (
          <EmptyState
            title="Not evaluated yet"
            description={
              isViewer
                ? "Builds are evaluated when runs are pushed. An editor can re-evaluate it now."
                : "Builds are evaluated when runs are pushed. Re-evaluate to compare this build with its baseline now."
            }
          />
        )
      ) : (
        <>
          {/* Top Status & Headline Banner */}
          <div className="rounded-lg border border-border/80 bg-card p-4 space-y-2 max-w-4xl shadow-2xs">
            <div className="flex items-start gap-3">
              <span className="mt-0.5 shrink-0">
                <StatusIcon status={result.status} />
              </span>
              <div className="space-y-1">
                <p className="text-base font-semibold text-foreground">
                  {result.headline}
                </p>
                {result.notes.length > 0 && (
                  <ul className="space-y-1.5 text-sm text-muted-foreground">
                    {result.notes.map((n) => (
                      <li key={n} className="flex items-start gap-2">
                        <span className="text-muted-foreground select-none leading-relaxed">
                          •
                        </span>
                        <span className="leading-relaxed">{n}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            {result.status === "inconclusive" && (
              <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-border/50 pt-3">
                <span className="text-xs font-medium text-foreground">
                  Next steps:
                </span>
                {result.coverage.scenarios.rerun <
                  result.coverage.scenarios.in_scope && (
                  <Button
                    asChild
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                  >
                    <Link to={`/projects/${project.id}/scenarios`}>
                      View unevaluated scenarios (
                      {result.coverage.scenarios.in_scope -
                        result.coverage.scenarios.rerun}{" "}
                      remaining) →
                    </Link>
                  </Button>
                )}
                {data.runs.length > 0 && (
                  <Button
                    asChild
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-muted-foreground hover:text-foreground"
                  >
                    <Link
                      to={`/projects/${project.id}/walkthroughs?build=${build.id}`}
                    >
                      View runs in build ({data.runs.length}) →
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Compact Comparison & Baseline Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/80 bg-card/60 px-4 py-2.5 text-xs max-w-4xl">
            <div className="flex items-center gap-2 text-muted-foreground">
              <span className="font-medium text-foreground">Baseline:</span>
              <span className="text-foreground">
                {result.baseline.label ??
                  BASELINE_MODE_LABELS[result.baseline.mode] ??
                  result.baseline.mode}
                {result.baseline.label &&
                  ` (${BASELINE_MODE_LABELS[result.baseline.mode] ?? result.baseline.mode})`}
              </span>
              <span>·</span>
              <span className="hidden sm:inline">
                Carried forward per scenario when not re-run
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor="gate-baseline-mode"
                  className="text-xs text-muted-foreground whitespace-nowrap"
                >
                  Compare with
                </Label>
                <Select
                  value={mode}
                  onValueChange={(v) => setMode(v as Mode | typeof STORED)}
                >
                  <SelectTrigger
                    id="gate-baseline-mode"
                    className="h-8 w-auto min-w-48 text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={STORED}>Stored comparison</SelectItem>
                    <SelectItem value="previous_run">
                      {BASELINE_MODE_LABELS.previous_run}
                    </SelectItem>
                    <SelectItem value="previous_build">
                      {BASELINE_MODE_LABELS.previous_build}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-1.5">
                <Label
                  htmlFor="gate-baseline-env"
                  className="text-xs text-muted-foreground whitespace-nowrap"
                >
                  Environment
                </Label>
                <Select
                  value={environment}
                  onValueChange={(v) => setEnvOverride(v as Env)}
                >
                  <SelectTrigger
                    id="gate-baseline-env"
                    className="h-8 w-auto min-w-48 text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="same">Same environment only</SelectItem>
                    <SelectItem value="any">Any environment</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {adHoc && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 px-2 text-xs"
                  onClick={() => {
                    setMode(STORED);
                    setEnvOverride(null);
                  }}
                >
                  Reset
                </Button>
              )}
            </div>
          </div>

          {/* Primary Section: What Changed in this Build */}
          <Section title="Findings by bucket">
            <GateBuckets
              result={result}
              projectId={project.id}
              buildId={build.id}
              scenarios={data.scenarios}
              findingDescriptions={data.findingDescriptions}
              canEdit={canEdit}
              onReevaluate={() =>
                evaluate.mutate({
                  projectId: project.id,
                  buildId: build.id,
                })
              }
            />
          </Section>

          {/* Scenario & Human Test Coverage */}
          <Section title="Coverage">
            <GateCoverage
              result={result}
              scenarios={data.scenarios}
              runsByScenario={runsByScenario}
              projectId={project.id}
            />
          </Section>

          {/* Gate Conditions / Rules Checklist */}
          <Section title="Conditions">
            <GateConditionsTable result={result} projectId={project.id} />
          </Section>

          {historyRaw && historyRaw.length > 0 && (
            <Section title="History">
              <GateHistoryList
                projectId={project.id}
                history={historyRaw as unknown as GateHistoryView[]}
                currentBuildId={build.id}
                label="Recent gates on this branch and environment"
              />
            </Section>
          )}
        </>
      )}
    </Page>
  );
}
